import { isNumber, isString } from "lodash";

const normalizeCondition = (condition) =>
  String(condition).replace(/\s+/g, " ").trim();

/**
 * Skip a rule value when it is empty, non-numeric falsey, the literal string
 * "undefined" (from `'${missingField}'` templates), or the legacy "null"
 * interpolation pattern.
 */
export const shouldSkipInsurancePlanRuleValue = (value) => {
  if (isString(value)) {
    const trimmed = value.trim();
    if (trimmed.length === 0) return true;
    if (trimmed.toLowerCase() === "undefined") return true;
    if (trimmed.includes("null")) return true;
  }
  return !isNumber(value) && !value;
};

/**
 * Builds one entry for the GraphQL `rules: [InsurancePlanRuleFilterInput!]`
 * list. The API expands these into parameterized EXISTS / NOT EXISTS SQL.
 */
export const buildInsurancePlanRule = ({
  rule,
  predicate,
  value,
  required = false,
}) => {
  if (predicate === "truthy") {
    return { rule, predicate, required: true };
  }
  if (shouldSkipInsurancePlanRuleValue(value)) {
    return null;
  }
  return { rule, predicate, required, value };
};

const unwrapQuotedString = (value) => {
  if (typeof value !== "string") return value;
  const t = value.trim();
  if (t.length >= 2 && t.startsWith("'") && t.endsWith("'")) {
    return t.slice(1, -1).replace(/''/g, "'");
  }
  return value;
};

/**
 * Maps a legacy SQL-style condition string to an `InsurancePlanRuleFilterInput`
 * via {@link buildInsurancePlanRule}. Returns a rule object or null.
 */
export const generateSQLCondition = (ruleName, condition, value) => {
  const c = normalizeCondition(condition);
  const v = unwrapQuotedString(value);

  if (c.includes("CAST(r.value AS NUMERIC) <=")) {
    return buildInsurancePlanRule({
      rule: ruleName,
      predicate: "numericLte",
      value: v,
    });
  }
  if (
    c.includes("CAST(r.value AS NUMERIC) >=") &&
    c.includes("CAST(r.value AS NUMERIC) = 0")
  ) {
    return buildInsurancePlanRule({
      rule: ruleName,
      predicate: "numericGteOrZero",
      value: v,
    });
  }
  if (c.includes("CAST(r.value AS NUMERIC) >=")) {
    return buildInsurancePlanRule({
      rule: ruleName,
      predicate: "numericGte",
      value: v,
    });
  }
  if (c.includes("to_jsonb(ARRAY") || c.includes("jsonb_array_length")) {
    return buildInsurancePlanRule({
      rule: ruleName,
      predicate: "jsonbArrayIncludes",
      value: v,
    });
  }
  if (
    c.includes("r.value IS NULL OR r.value =") ||
    /\br\.value\s*=\s*:value\b/.test(c)
  ) {
    return buildInsurancePlanRule({
      rule: ruleName,
      predicate: "stringEq",
      value: v,
    });
  }
  return null;
};

/**
 * Builds a required truthy rule (same shape as {@link buildInsurancePlanRule}
 * with predicate "truthy").
 */
export const generateForcedSQLCondition = (ruleName, _condition, _value) =>
  buildInsurancePlanRule({
    rule: ruleName,
    predicate: "truthy",
    required: true,
  });
